import { privileges } from 'Components/constants/appConstants';
import ComparisionTool from 'pages/Client/ComparisionTool';
import ThreeDTool from 'pages/Client/ThreeDTool';
import AddUser from 'pages/Usermanagement/AddUser';
import EditUser from 'pages/Usermanagement/EditUser';
import ViewUser from 'pages/Usermanagement/ViewUser';
import ViewUserRequests from 'pages/Usermanagement/ViewUserRequests';
/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react';
import { Navigate } from 'react-router-dom';

// Feature flag — controlled via REACT_APP_SHOW_HOME_PAGE in .env
// Set to "true" to display the SCCL home page at "/" before login.
// Set to "false" (or omit) to preserve the previous direct-to-login behaviour.
const SHOW_HOME_PAGE = process.env.REACT_APP_SHOW_HOME_PAGE === 'true';
// import ClientAreas from 'pages/Client/ClientAreas';
// import ClientAreaMap from 'pages/Client/ClientAreaMap';

// Error Pages
const Basic404 = React.lazy(() => import('../pages/Errors/Basic404'));
const Cover404 = React.lazy(() => import('../pages/Errors/Cover404'));
const Alt404 = React.lazy(() => import('../pages/Errors/Alt404'));
const Error500 = React.lazy(() => import('../pages/Errors/Error500'));

// Authentication Pages
const Login = React.lazy(() => import('../pages/Authentication/Login'));
const ForgetPasswordPage = React.lazy(() => import('../pages/Authentication/ForgetPassword'));
const Logout = React.lazy(() => import('../pages/Authentication/Logout'));
const SignUp = React.lazy(() => import('pages/Authentication/SignUp'));
const ChooseRole = React.lazy(() => import('pages/Authentication/ChooseRole'));
const ForgetPasswordVerification = React.lazy(() => import('pages/Authentication/ForgetPasswordVerification'));
// const Register = React.lazy(() => import('../pages/Authentication/Register'));
// const UserProfile = React.lazy(() => import('../pages/Authentication/user-profile'));

// Home Page (public landing page, shown before login when flag is ON)
const HomePage = React.lazy(() => import('pages/HomePage'));

// Main Pages
const Dashboard = React.lazy(() => import('pages/Dashboard/Dashboard'));
const Area = React.lazy(() => import('pages/Area/Area'));
const Mines = React.lazy(() => import('pages/Area/Mines/Mines'));
const AddArea = React.lazy(() => import('pages/Area/AddArea'));
const Region = React.lazy(() => import('pages/Region/Region'));
const AddRegion = React.lazy(() => import('pages/Region/AddRegion'));
const RegionDetails = React.lazy(() => import('pages/Region/RegionDetails'));
const AddMines = React.lazy(() => import('pages/Area/Mines/AddMines'));
const Sources = React.lazy(() => import('pages/Area/Sources_Layers/Sources'));
const ReportsDownloads = React.lazy(() => import('pages/Area/Deliverables/ReportsDownloads'));
const EmployeeId = React.lazy(() => import('pages/EmployeeId/EmployeeId'));
const UserManagement = React.lazy(() => import('pages/Usermanagement/UserManagement'));
const Logs = React.lazy(() => import('pages/Logs/Logs'));
const ClientAreas = React.lazy(() => import('pages/Client/ClientAreas'));
const ClientAreaMap = React.lazy(() => import('pages/Client/ClientAreaMap'));
const Departments = React.lazy(() => import('pages/Departments'));
const AdminMapView = React.lazy(() => import('pages/Client/AdminMapView'));
const NodalOfficerMapView = React.lazy(() => import('pages/Client/NodalOfficerMapView'));

const authProtectedRoutes = [
    // User Profile
    // { path: '/profile', component: <UserProfile /> },
    { path: '/dashboard', component: <Dashboard />, view: privileges.DASHBOARD.DASHBOARD },

    { path: '/allarea/addarea', component: <AddArea />, create: privileges.AREAS.ADD_AREAS },
    {
        path: '/allarea/:id/mines',
        component: <Mines />,
        create: privileges.MINES.ADD_MINES,
        view: privileges.MINES.VIEW_MINES,
        edit: privileges.MINES.EDIT_MINES,
        delete: privileges.MINES.DELETE_MINES
    },
    { path: '/allarea/addmine', component: <AddMines />, create: privileges.MINES.ADD_MINES },
    {
        path: '/area',
        view: privileges.AREAS.VIEW_AREAS,
        create: privileges.AREAS.ADD_AREAS,
        delete: privileges.AREAS.DELETE_AREAS,
        component: <Area />
    },
    {
        path: '/regions',
        view: privileges.AREAS.VIEW_AREAS,
        create: privileges.AREAS.ADD_AREAS,
        edit: privileges.AREAS.EDIT_AREAS,
        delete: privileges.AREAS.DELETE_AREAS,
        component: <Region />
    },
    { path: '/regions/add-region', component: <AddRegion />, create: privileges.AREAS.ADD_AREAS },
    {
        path: '/regions/:id/areas',
        view: privileges.AREAS.VIEW_AREAS,
        create: privileges.AREAS.ADD_AREAS,
        edit: privileges.AREAS.EDIT_AREAS,
        delete: privileges.AREAS.DELETE_AREAS,
        component: <RegionDetails />
    },
    {
        path: '/user-management',
        create: privileges.USERS.ADD_USERS,
        view: privileges.USERS.VIEW_USERS,
        edit: privileges.USERS.EDIT_USERS,
        delete: privileges.USERS.DELETE_USERS,
        component: <UserManagement />
    },
    {
        path: '/user-management-new-request',
        create: privileges.USERS.ADD_USERS,
        view: privileges.USERS.VIEW_USERS,
        edit: privileges.USERS.EDIT_USERS,
        delete: privileges.USERS.DELETE_USERS,
        component: <UserManagement />
    },
    {
        path: '/user-management/addUser',
        create: privileges.USERS.ADD_USERS,
        component: <AddUser />
    },
    {
        path: '/user-management/allUser/EditUser/:id',
        edit: privileges.USERS.EDIT_USERS,
        component: <EditUser />
    },
    {
        path: '/user-management/allUser/viewUser/:id',
        view: privileges.USERS.VIEW_USERS,
        component: <ViewUser />
    },
    {
        path: '/user-management/newUserRequests/viewUser/:id',
        view: privileges.USERS.VIEW_USERS,
        component: <ViewUserRequests />
    },
    {
        path: '/allarea/sources/:id',
        create: privileges.SOURCES.ADD_SOURCES,
        view: privileges.SOURCES.VIEW_SOURCES,
        edit: privileges.SOURCES3D.EDIT_3D_LAYER,
        delete: privileges.SOURCES3D.DELETE_3D_LAYER,
        component: <Sources />
    },
    {
        path: '/area/deliverables/:id',
        view: privileges.REPORTS.VIEW_REPORTS,
        create: privileges.REPORTS.ADD_REPORTS,
        delete: privileges.DOWNLOADS.DELETE_RECORDS,
        component: <ReportsDownloads />
    },
    {
        path: '/employeeId',
        create: privileges.EMPLOYEEID.ADD_EMPLOYEE_IDS,
        view: privileges.EMPLOYEEID.VIEW_EMPLOYEE_IDS,
        edit: privileges.EMPLOYEEID.EDIT_EMPLOYEE_IDS,
        delete: privileges.EMPLOYEEID.DELETE_EMPLOYEE_IDS,
        component: <EmployeeId />
    },
    {
        path: '/information-management',
        view: privileges.ENTRIES.VIEW_INFORMATION_MANAGEMENT,
        create: privileges.ENTRIES.VIEW_MODIFIED_ENTRIES,
        delete: privileges.ENTRIES.VIEW_NEWLY_ADDED_ENTRIES,
        component: <Logs />
    },
    {
        path: '/departments',
        view: privileges.DEPARTMENTS.DEPARTMENTS, // Temporarily matching standard admin/view rules
        component: <Departments />
    },
    // { path: '/clientAreaDashboard', component: <ClientAreas />, view: 'VIEW_CLIENT_AREAS' },
    // { path: '/clientMap', component: <ClientAreaMap />, view: 'MAP_VIEW' },
    // { path: '/3dmodal', component: <ThreeDTool />, view: '3D_SCENE' },


    // When the home page flag is ON the "/" public route takes precedence,
    // so we only register the auth-protected root redirect when flag is OFF.
    ...(!SHOW_HOME_PAGE
        ? [{ path: '/', exact: true, view: privileges.AREAS.VIEW_AREAS, component: <Navigate to="/regions" /> }]
        : []
    ),
    // path="*" catch-all lives in Routes/index.tsx, outside AuthProtected.
];

const publicRoutes = [
    // Home Page — public landing page before login (flag-controlled)
    ...(SHOW_HOME_PAGE ? [{ path: '/home', component: <HomePage /> }] : []),
    // Authentication Page
    { path: '/logout', component: <Logout /> },
    { path: '/login', component: SHOW_HOME_PAGE ? <Navigate to="/home" replace /> : <Login /> },
    { path: '/signup', component: <SignUp /> },
    { path: '/forgot-password', component: <ForgetPasswordPage /> },
    { path: '/forgot-password-verification', component: <ForgetPasswordVerification /> },
    // { path: '/register', component: <Register /> },
    { path: '/auth-404-basic', component: <Basic404 /> },
    { path: '/auth-404-cover', component: <Cover404 /> },
    { path: '/auth-404-alt', component: <Alt404 /> },
    { path: '/auth-500', component: <Error500 /> }
];

const onlyHeaderRoutes = [
    { path: '/choose-role', multiRole: 'CHOOSE_ROLE', component: <ChooseRole /> },
    { path: '/clientAreaDashboard', component: <ClientAreas />, view: 'VIEW_CLIENT_AREAS' },
    { path: '/clientMap', component: <ClientAreaMap />, view: 'MAP_VIEW' },
    { path: '/3dmodal', component: <ThreeDTool />, view: '3D_SCENE', create: 'VIEW_3D_LAYER' },
    { path: '/admin-map-view', component: <AdminMapView />, view: privileges.CLIENT_MAP.ADMIN_MAP_VIEW },
    { path: '/nodal-officer-map-view', component: <NodalOfficerMapView />, view: privileges.CLIENT_MAP.NODAL_OFFICER_MAP_VIEW },
    {
        path: '/comparision', component: <ComparisionTool />,
        view: 'COMPARISION' ,
        create: privileges.CLIENT_MAP.ADMIN_MAP_VIEW,
        delete: privileges.CLIENT_MAP.NODAL_OFFICER_MAP_VIEW,
    },
];

export { authProtectedRoutes, publicRoutes, onlyHeaderRoutes, SHOW_HOME_PAGE };
